import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { getStudent } from '../academyApi';
import { addDays, todayKST } from '../periodDates';
import { notifyAcademyChanged } from '../ui';
import { DateInput, EmptyCodesNote, Field, FormModal, MultiSelectInput, Note, SelectInput, TextArea, useClassList, useCodeList } from './form';
import { activePause, pauseEnrollmentPlan, savePauseAndClasses, saveResumeAndClasses } from './pauseWorkflow';

/** 부분 저장 뒤 닫아도 목록을 갱신한다. 실패 후 재시도는 반드시 읽기부터 시작한다. */
function useWorkflow(detail, gat, onClose) {
  const [snapshot, setSnapshot] = useState(detail);
  const [attempted, setAttempted] = useState(false);
  const [refreshNeeded, setRefreshNeeded] = useState(false);
  const [progress, setProgress] = useState('');
  const dirty = useRef(false);
  useEffect(() => () => { if (dirty.current) notifyAcademyChanged(); }, []);
  const read = async () => {
    const latest = await getStudent(detail.student_id, {}, gat);
    setSnapshot(latest);
    setRefreshNeeded(false);
  };
  const run = async (save) => {
    if (refreshNeeded) { await read(); return false; }
    setAttempted(true);
    try {
      await save({ detail: snapshot, onSnapshot: setSnapshot, onProgress: setProgress, onWrite: () => { dirty.current = true; } });
      dirty.current = false;
      return true;
    } catch (error) {
      setProgress('완료된 단계는 유지됩니다. 학생 정보를 다시 읽은 뒤 남은 작업만 처리합니다.');
      setRefreshNeeded(true);
      throw error;
    }
  };
  return { snapshot, attempted, refreshNeeded, progress, run,
    close: () => { if (dirty.current) { dirty.current = false; notifyAcademyChanged(); } onClose(); } };
}

function ClassCleanup({ rows, startOn }) {
  if (!rows.length) return null;
  return <div className="space-y-1 rounded-xl bg-gray-50 px-3 py-2 text-xs text-gray-700">
    <p className="font-medium">함께 처리할 반</p>
    <ul className="space-y-1">{rows.map((row) => <li key={row.id}>
      {row.class_name} · {row.operation === 'cancel' ? `${row.start_on} 입반 취소` : `${addDays(startOn, -1)}까지 수강 후 퇴반`}
    </li>)}</ul>
    {rows.some((row) => row.operation === 'cancel') && <p className="text-yellow-800">휴원일 이후의 입반은 취소합니다. 반 이동 예정도 취소하고 앞 반은 휴원 전날로 끝냅니다.</p>}
  </div>;
}

export function PauseForm({ detail, ...modal }) {
  const { getAccessToken } = useAuth();
  const reasons = useCodeList('pause_reason');
  const workflow = useWorkflow(detail, getAccessToken, modal.onClose);
  const existing = activePause(workflow.snapshot);
  const [startOn, setStartOn] = useState(() => activePause(detail)?.start_on || todayKST());
  const [reason, setReason] = useState('');
  const [memo, setMemo] = useState('');
  const rows = pauseEnrollmentPlan(workflow.snapshot, startOn);
  const valid = startOn >= (workflow.snapshot.current.tenure?.start_on || '') && startOn <= todayKST()
    && (existing ? existing.start_on === startOn : reasons.items.some((r) => r.code === reason));
  const submitLabel = workflow.refreshNeeded ? '학생 정보 다시 불러오기' : existing ? '남은 반 처리' : '휴원·퇴반 저장';
  return <FormModal {...modal} onClose={workflow.close} lockWhileBusy submitLabel={submitLabel}
    cancelLabel={workflow.attempted ? '닫기' : '취소'} canSubmit={workflow.refreshNeeded || valid}
    onSubmit={() => workflow.run((callbacks) => savePauseAndClasses({ ...callbacks, startOn, reasonCode: reason, memo }, getAccessToken))}>
    {existing && <Note>휴원은 저장되어 있습니다. 남은 반 편성만 처리합니다.</Note>}
    <Field label="휴원 시작일" required hint="이날부터 수강하지 않습니다. 반의 마지막 수강일은 전날입니다.">
      <DateInput value={startOn} onChange={setStartOn} min={detail.current.tenure?.start_on || undefined} max={todayKST()} disabled={workflow.refreshNeeded || !!existing} />
    </Field>
    {!existing && <>
      <Field label="휴원 사유" required>
        <SelectInput value={reason} onChange={setReason} disabled={workflow.refreshNeeded} options={reasons.items.map((r) => [r.code, r.label])} placeholder="휴원 사유 선택" />
        <EmptyCodesNote list={reasons} label="휴원 사유" required />
      </Field>
      <Field label="메모"><TextArea value={memo} onChange={setMemo} disabled={workflow.refreshNeeded} rows={2} /></Field>
    </>}
    <ClassCleanup rows={rows} startOn={startOn} />
    {!rows.length && <Note>함께 퇴반할 반이 없습니다.</Note>}
    {workflow.progress && <p role="status" className="text-xs text-gray-600">{workflow.progress}</p>}
    {workflow.attempted && <Note>중간에 닫으면 학생의 [작업 → 휴원 반 정리]에서 남은 반을 처리할 수 있습니다.</Note>}
  </FormModal>;
}

export function ResumeForm({ detail, ...modal }) {
  const { getAccessToken } = useAuth();
  const classes = useClassList('all');
  const workflow = useWorkflow(detail, getAccessToken, modal.onClose);
  const [pauseId] = useState(() => activePause(detail)?.id);
  const pause = workflow.snapshot.pauses.find((p) => p.id === pauseId);
  const [endOn, setEndOn] = useState(todayKST());
  const [classIds, setClassIds] = useState([]);
  const open = activePause(workflow.snapshot);
  const cleanup = open?.id === pauseId ? pauseEnrollmentPlan(workflow.snapshot, open.start_on) : [];
  const resumed = pause?.end_on === endOn && !pause?.auto_closed;
  const options = classes.filter((c) => c.active || classIds.includes(String(c.class_id))).map((c) => [String(c.class_id), `${c.name} · ${c.teacher?.name || '담임 없음'}`]);
  const valid = !!pause && endOn > pause.start_on && endOn <= todayKST()
    && (!detail.current.withdraw_scheduled_on || endOn < detail.current.withdraw_scheduled_on);
  return <FormModal {...modal} onClose={workflow.close} lockWhileBusy
    cancelLabel={workflow.attempted ? '닫기' : '취소'}
    submitLabel={workflow.refreshNeeded ? '학생 정보 다시 불러오기' : resumed ? '남은 반 입반' : '복귀·입반 저장'}
    canSubmit={workflow.refreshNeeded || valid}
    onSubmit={() => workflow.run((callbacks) => saveResumeAndClasses({ ...callbacks, pauseId, endOn, classIds }, getAccessToken))}>
    {pause ? <Note>휴원 {pause.start_on}부터{pause.reason ? ` · ${pause.reason}` : ''}</Note> : <Note tone="warn">열린 휴원 기록이 없습니다.</Note>}
    {pause && pause.start_on >= todayKST() && <Note tone="warn">오늘 시작한 휴원은 내일부터 복귀할 수 있습니다.</Note>}
    {pause?.auto_closed && <Note>퇴원 예정 {addDays(pause.end_on, -1)}까지 휴원 중입니다.</Note>}
    <Field label="복귀일 — 다시 나오는 첫날" required>
      <DateInput value={endOn} onChange={setEndOn} min={pause ? addDays(pause.start_on, 1) : undefined}
        max={detail.current.withdraw_scheduled_on ? [todayKST(), addDays(detail.current.withdraw_scheduled_on, -1)].sort()[0] : todayKST()}
        disabled={workflow.refreshNeeded || resumed} />
    </Field>
    <Field label="복귀할 반" hint="선택한 반에 복귀일로 입반합니다. 선택하지 않으면 복귀만 저장합니다.">
      <MultiSelectInput value={classIds} onChange={setClassIds} options={options} disabled={workflow.refreshNeeded} placeholder="반 선택" />
    </Field>
    <ClassCleanup rows={cleanup} startOn={pause?.start_on} />
    {resumed && <Note>복귀는 저장되어 있습니다. 선택한 반 중 이미 입반한 반은 건너뜁니다.</Note>}
    {workflow.progress && <p role="status" className="text-xs text-gray-600">{workflow.progress}</p>}
    {workflow.attempted && <Note>입반이 끝나기 전에 닫으면 [작업 → 입반(반 추가)]에서 남은 반을 추가할 수 있습니다.</Note>}
  </FormModal>;
}
